import React, { createContext, useContext } from 'react';
import { useSelect, useSimpleList } from '@refinedev/antd';
import { BaseRecord, useOne, useTranslate } from '@refinedev/core';

import { useRulesContext } from 'contexts/rulesContext/RulesContext';
import { useTokenContext } from 'contexts/resource/ResourceContext';
import apiEndpoint from 'core/common/apiEndPoints';
import replacePipe from 'core/utils/replacePipe';

const RuleChangeDataContext = createContext<any>({});
export const useRuleChangeData = () => useContext(RuleChangeDataContext);

export const RuleChangeDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const t = useTranslate();
  const { rulePlanDataObj } = useRulesContext();
  const { userDetails } = useTokenContext();
  const unifiedId = rulePlanDataObj?.unified_source_master_id;
  const programId = userDetails?.iprogram;

  // Unified column list (was getUnifiedList in RuleForm)
  const { listProps: unifiedColumnProps } = useSimpleList({
    dataProviderName: 'unifiedDataProvider',
    resource: `${apiEndpoint.dataSourceColumnList}/?id=${unifiedId}&source_type=unified&is_rule=true`,
    meta: { type: 'select' },
    pagination: { mode: 'off' },
    queryOptions: { enabled: !!unifiedId },
    errorNotification: (data: BaseRecord) => ({
      message: t('notifications.mutationError'),
      description:
        replacePipe(data?.message) || t('notifications.errorMessage'),
      type: 'error',
    }),
  });

  // Unified source dropdown (was in RulePlanStrip)
  const { selectProps: unifiedSelectProps } = useSelect({
    resource: `${apiEndpoint.getProgrambasedUnifiedSource}/?page=1&page_size=1000`,
    dataProviderName: 'unifiedDataProvider',
    optionLabel: 'unified_source_name',
    optionValue: 'unique_id',
    meta: { type: 'select' },
  });

  // Incentive program → rule_approval (was in RulePlanStrip)
  const { data: ipData } = useOne({
    dataProviderName: 'organizationDataProvider',
    resource: apiEndpoint.incentiveProgram,
    id: programId,
    queryOptions: { enabled: !!programId },
  });

  const value = {
    unifiedListUnfiltered: (unifiedColumnProps?.dataSource as any)?.column_list,
    unifiedList: (unifiedSelectProps as any)?.options || [],
    isRuleApproval: ipData?.data?.results?.rule_approval,
  };

  return (
    <RuleChangeDataContext.Provider value={value}>
      {children}
    </RuleChangeDataContext.Provider>
  );
};
